import './customer-checklist-work.css';
import { useTranslation } from 'react-i18next';
import { CHECKLIST_KINDS, type ChecklistDayRule, type ChecklistDefinition } from '@shared/checklists/stack.ts';
import { SERVICE_FREQUENCIES } from '@shared/constants/index.ts';
import { Button, Select, TextInput, TextArea } from './ui.tsx';

export const newChecklistDefinition = (): ChecklistDefinition => ({
  name: '',
  nameTranslations: null,
  kind: 'custom',
  sections: [
    {
      id: crypto.randomUUID(),
      name: '',
      nameTranslations: null,
      rule: { weekdays: [], everyNthVisit: null, frequency: 'every_visit' },
      tasks: [newTask()],
    },
  ],
});
const newTask = (): ChecklistDefinition['sections'][number]['tasks'][number] => ({
  id: crypto.randomUUID(),
  name: '',
  nameTranslations: null,
  description: null,
  descriptionTranslations: null,
  estimatedMinutes: 0,
  requiresPhoto: false,
  rule: { weekdays: [], everyNthVisit: null, frequency: 'every_visit' },
});
export function ChecklistRuleEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: ChecklistDayRule;
  onChange: (value: ChecklistDayRule) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const everyOther = value.everyNthVisit === 2;
  return (
    <div className="space-y-2">
      {/* The common case, one tap: deep baseboards on visits 2, 4, 6… The
          number box below takes any other N. */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={everyOther ? 'primary' : 'ghost'}
          aria-pressed={everyOther}
          disabled={disabled}
          onClick={() => onChange({ ...value, everyNthVisit: everyOther ? null : 2 })}
        >
          {t('checklistStack.everyOther')}
        </Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select
          label={t('checklistStack.frequency')}
          value={value.frequency}
          disabled={disabled}
          onChange={(event) => onChange({ ...value, frequency: event.target.value as ChecklistDayRule['frequency'] })}
          options={SERVICE_FREQUENCIES.map((frequency) => ({
            value: frequency,
            label: t(`servicesStep.frequency.${frequency}`),
          }))}
        />
        <TextInput
          type="number"
          min={1}
          max={365}
          label={t('checklistStack.nth')}
          hint={t('checklistStack.nthHint')}
          value={value.everyNthVisit ?? ''}
          disabled={disabled}
          onChange={(event) =>
            onChange({ ...value, everyNthVisit: event.target.value ? Number(event.target.value) : null })
          }
        />
      </div>
      <fieldset disabled={disabled} className="flex flex-wrap gap-x-3 gap-y-2">
        <legend className="mb-1 text-sm font-medium">{t('checklistStack.weekdays')}</legend>
        {Array.from({ length: 7 }, (_, day) => (
          <label key={day} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={value.weekdays.includes(day)}
              onChange={() =>
                onChange({
                  ...value,
                  weekdays: value.weekdays.includes(day)
                    ? value.weekdays.filter((value) => value !== day)
                    : [...value.weekdays, day].sort(),
                })
              }
            />
            {t(`checklistStack.days.${day}`)}
          </label>
        ))}
      </fieldset>
      <p className="text-xs text-[color:var(--color-muted)]">{t('checklistStack.rulesHint')}</p>
    </div>
  );
}
export function ChecklistDefinitionEditor({
  value,
  onChange,
  photoAllowed,
  disabled = false,
}: {
  value: ChecklistDefinition;
  onChange: (value: ChecklistDefinition) => void;
  photoAllowed: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const sectionChange = (index: number, section: ChecklistDefinition['sections'][number]) =>
    onChange({
      ...value,
      sections: value.sections.map((current, position) => (position === index ? section : current)),
    });
  return (
    <fieldset disabled={disabled} className="checklist-work-editor space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput
          label={t('checklistStack.name')}
          value={value.name}
          maxLength={120}
          onChange={(event) => onChange({ ...value, name: event.target.value })}
        />
        <TextInput
          label={t('checklistStack.nameEs')}
          value={value.nameTranslations?.es ?? ''}
          maxLength={120}
          onChange={(event) =>
            onChange({ ...value, nameTranslations: event.target.value ? { es: event.target.value } : null })
          }
        />
        <Select
          label={t('checklistStack.kind')}
          value={value.kind}
          onChange={(event) => onChange({ ...value, kind: event.target.value as ChecklistDefinition['kind'] })}
          options={CHECKLIST_KINDS.map((kind) => ({ value: kind, label: t(`checklistStack.kinds.${kind}`) }))}
        />
      </div>
      {value.sections.map((section, index) => (
        <section key={section.id} className="checklist-work-section space-y-3">
          <h3 className="font-semibold">
            {index + 1}. {section.name || t('checklistStack.sectionName')}
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <TextInput
              label={t('checklistStack.sectionName')}
              value={section.name}
              maxLength={200}
              onChange={(event) => sectionChange(index, { ...section, name: event.target.value })}
            />
            <TextInput
              label={t('checklistStack.sectionEs')}
              value={section.nameTranslations?.es ?? ''}
              maxLength={200}
              onChange={(event) =>
                sectionChange(index, {
                  ...section,
                  nameTranslations: event.target.value ? { es: event.target.value } : null,
                })
              }
            />
          </div>
          <ChecklistRuleEditor value={section.rule} onChange={(rule) => sectionChange(index, { ...section, rule })} />
          {section.tasks.map((task, taskIndex) => {
            const change = (next: typeof task) =>
              sectionChange(index, {
                ...section,
                tasks: section.tasks.map((current, position) => (position === taskIndex ? next : current)),
              });
            return (
              <div key={task.id} className="checklist-work-task space-y-2">
                <h4 className="font-semibold">
                  {taskIndex + 1}. {task.name || t('checklistStack.taskName')}
                </h4>
                <div className="grid gap-2 sm:grid-cols-2">
                  <TextInput
                    label={t('checklistStack.taskName')}
                    value={task.name}
                    maxLength={200}
                    onChange={(event) => change({ ...task, name: event.target.value })}
                  />
                  <TextInput
                    label={t('checklistStack.taskEs')}
                    value={task.nameTranslations?.es ?? ''}
                    maxLength={200}
                    onChange={(event) =>
                      change({ ...task, nameTranslations: event.target.value ? { es: event.target.value } : null })
                    }
                  />
                  <TextInput
                    label={t('checklistStack.minutes')}
                    type="number"
                    min={0}
                    max={1440}
                    value={task.estimatedMinutes}
                    onChange={(event) => change({ ...task, estimatedMinutes: Number(event.target.value) })}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={task.requiresPhoto}
                      disabled={!photoAllowed}
                      onChange={(event) => change({ ...task, requiresPhoto: event.target.checked })}
                    />
                    {t('checklists.requiresPhoto')}
                  </label>
                </div>
                <TextArea
                  label={t('checklistStack.description')}
                  value={task.description ?? ''}
                  maxLength={2000}
                  onChange={(event) => change({ ...task, description: event.target.value || null })}
                />
                <TextArea
                  label={t('checklistStack.descriptionEs')}
                  value={task.descriptionTranslations?.es ?? ''}
                  maxLength={2000}
                  onChange={(event) =>
                    change({ ...task, descriptionTranslations: event.target.value ? { es: event.target.value } : null })
                  }
                />
                <ChecklistRuleEditor value={task.rule} onChange={(rule) => change({ ...task, rule })} />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={taskIndex === 0}
                    onClick={() => {
                      const tasks = [...section.tasks];
                      [tasks[taskIndex - 1], tasks[taskIndex]] = [tasks[taskIndex]!, tasks[taskIndex - 1]!];
                      sectionChange(index, { ...section, tasks });
                    }}
                  >
                    {t('checklistStack.up')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={taskIndex === section.tasks.length - 1}
                    onClick={() => {
                      const tasks = [...section.tasks];
                      [tasks[taskIndex + 1], tasks[taskIndex]] = [tasks[taskIndex]!, tasks[taskIndex + 1]!];
                      sectionChange(index, { ...section, tasks });
                    }}
                  >
                    {t('checklistStack.down')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={section.tasks.length === 1}
                    onClick={() =>
                      sectionChange(index, {
                        ...section,
                        tasks: section.tasks.filter((current) => current.id !== task.id),
                      })
                    }
                  >
                    {t('checklistStack.removeTask')}
                  </Button>
                </div>
              </div>
            );
          })}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => sectionChange(index, { ...section, tasks: [...section.tasks, newTask()] })}
            >
              {t('checklistStack.addTask')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={index === 0}
              onClick={() => {
                const sections = [...value.sections];
                [sections[index - 1], sections[index]] = [sections[index]!, sections[index - 1]!];
                onChange({ ...value, sections });
              }}
            >
              {t('checklistStack.up')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={index === value.sections.length - 1}
              onClick={() => {
                const sections = [...value.sections];
                [sections[index + 1], sections[index]] = [sections[index]!, sections[index + 1]!];
                onChange({ ...value, sections });
              }}
            >
              {t('checklistStack.down')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={value.sections.length === 1}
              onClick={() =>
                onChange({ ...value, sections: value.sections.filter((current) => current.id !== section.id) })
              }
            >
              {t('checklistStack.removeSection')}
            </Button>
          </div>
        </section>
      ))}
      <Button
        type="button"
        variant="secondary"
        onClick={() =>
          onChange({ ...value, sections: [...value.sections, { ...newChecklistDefinition().sections[0]! }] })
        }
      >
        {t('checklistStack.addSection')}
      </Button>
    </fieldset>
  );
}
